/**
 * Web EngineConnectorPanel — thin wrapper around the shared component.
 *
 * Defines Web-specific modes (local / cloud / none)
 * and maps the Web connector store to the shared component's props.
 */

import {
    EngineConnectorPanel as BasePanel,
    type ConnectorModeConfig,
    type ConnectorPanelCallbacks,
    type ConnectorPanelState,
    useT,
} from "@magic-md/editor-core";
import { Globe, HardDrive, WifiOff } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useEngineConnectorStore } from "../platform/engine-connector";

export function EngineConnectorPanel() {
    const t = useT("editor");
    const mode = useEngineConnectorStore((s) => s.mode);
    const connected = useEngineConnectorStore((s) => s.connected);
    const error = useEngineConnectorStore((s) => s.error);
    const connecting = useEngineConnectorStore((s) => s.connecting);
    const connectMessage = useEngineConnectorStore((s) => s.connectMessage);
    const probeResult = useEngineConnectorStore((s) => s.probeResult);
    const probing = useEngineConnectorStore((s) => s.probing);
    const localEndpoint = useEngineConnectorStore((s) => s.localEndpoint);
    const setMode = useEngineConnectorStore((s) => s.setMode);
    const setLocalEndpoint = useEngineConnectorStore((s) => s.setLocalEndpoint);
    const probe = useEngineConnectorStore((s) => s.probe);
    const storeConnect = useEngineConnectorStore((s) => s.connect);
    const cancelConnect = useEngineConnectorStore((s) => s.cancelConnect);
    const disconnect = useEngineConnectorStore((s) => s.disconnect);
    const refreshStatus = useEngineConnectorStore((s) => s.refreshStatus);

    // Refresh once when the popover opens while already connected.
    useEffect(() => {
        if (!connected) return;
        void refreshStatus();
    }, [connected, refreshStatus]);

    const modes = useMemo<ConnectorModeConfig[]>(
        () => [
            {
                id: "local",
                label: t("connector.mode.local"),
                icon: HardDrive,
                connectedLabel: t("connector.mode.localConnected"),
                connectedAccent: "success",
                endpoint: {
                    placeholder: "http://localhost:3721",
                    showProbe: true,
                    helpText: t("connector.localHelp", { command: "magic-md connect" }),
                },
            },
            {
                id: "cloud",
                label: t("connector.mode.cloud"),
                icon: Globe,
                connectedLabel: t("connector.mode.cloudConnected"),
                connectedAccent: "info",
                helpText: t("connector.mode.cloudHelp"),
            },
            {
                id: "none",
                label: t("connector.mode.none"),
                icon: WifiOff,
                connectedLabel: t("connector.notConnected"),
            },
        ],
        [t],
    );

    // Map store state to shared component state
    const state: ConnectorPanelState = {
        currentMode: mode,
        connected,
        error,
        connecting,
        connectMessage,
        endpoint: localEndpoint,
        status: {
            alive: probeResult?.alive ?? connected,
            ready: probeResult?.ready ?? connected,
        },
        probeResult: probeResult ? { ready: probeResult.ready } : null,
        probing,
    };

    const callbacks: ConnectorPanelCallbacks = useMemo(
        () => ({
            onModeSwitch: (modeId: string) => {
                if (connected) {
                    void disconnect();
                }
                setMode(modeId);
            },
            onEndpointChange: (ep: string) => {
                setLocalEndpoint(ep);
            },
            onConnect: async (ep: string) => {
                const currentMode = useEngineConnectorStore.getState().mode;
                if (currentMode === "local" && ep !== localEndpoint) {
                    setLocalEndpoint(ep);
                }
                await storeConnect();
            },
            onCancelConnect: cancelConnect,
            onDisconnect: async () => {
                await disconnect();
            },
            onProbe: async (ep: string) => {
                if (ep !== localEndpoint) {
                    setLocalEndpoint(ep);
                }
                await probe(ep);
            },
            onRefreshStatus: async () => {
                await refreshStatus({ force: true });
            },
        }),
        [
            cancelConnect,
            connected,
            disconnect,
            localEndpoint,
            probe,
            refreshStatus,
            setLocalEndpoint,
            setMode,
            storeConnect,
        ],
    );

    return (
        <BasePanel
            modes={modes}
            state={state}
            callbacks={callbacks}
            disconnectedHint={<p>{t("connector.web.disconnectedHint", { command: "magic-md connect" })}</p>}
        />
    );
}
